import { useState, useEffect, useCallback } from 'react';
import { useModal } from '../contexts';
import Modal from './Modal';
import { useTranslation } from 'react-i18next';
import { OAuthError, postData } from '../backend';
import OAuthModalMicrosoft from './OAuthModalMicrosoft';
import jwtDecode from 'jwt-decode';
import {
  SearchIcon,
  FolderIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  HomeIcon,
  ShareIcon,
  OfficeBuildingIcon,
  CollectionIcon,
} from '@heroicons/react/outline';
import * as graph from '../microsoftGraph';
import { MIMETYPE_ICON_MAP } from '../utils';
import useDebounce from '../hooks/useDebounce';

interface BreadcrumbEntry {
  id: string;
  name: string;
}

type LocationType = 'my-drive' | 'shared' | 'site-drive';

interface Props {
  onSelect: (fileId: string, driveId?: string) => void;
  allowedFileTypes?: string;
  mode?: 'file' | 'folder';
  title?: string;
}

function MicrosoftFileBrowser(props: Props) {
  const { closeModal, openModal } = useModal();
  const { t } = useTranslation();

  const mode = props.mode || 'file';

  // Auth
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [hasSharePointScope, setHasSharePointScope] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Navigation
  const [currentDriveId, setCurrentDriveId] = useState('');
  const [currentLocation, setCurrentLocation] = useState<LocationType>('my-drive');
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbEntry[]>([]);

  // Content
  const [items, setItems] = useState<graph.DriveItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<graph.DriveItem | null>(null);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const debouncedSearch: string = useDebounce(searchQuery, 500);

  // SharePoint
  const [sites, setSites] = useState<graph.Site[]>([]);
  const [siteDrives, setSiteDrives] = useState<Record<string, graph.Drive[]>>({});
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());

  // Allowed extensions
  const allowedExtensions = props.allowedFileTypes
    ? props.allowedFileTypes.split(',').map(ext => ext.trim().toLowerCase())
    : null;

  // Get access token
  useEffect(() => {
    setAuthLoading(true);
    postData('/microsoft/access-token/')
      .then((token: string) => {
        setAccessToken(token);
        try {
          const payload: Record<string, string> = jwtDecode(token);
          setHasSharePointScope(payload['scp']?.includes('Sites.ReadWrite.All') || false);
        } catch { }
        setAuthLoading(false);
      })
      .catch((err) => {
        if (err instanceof OAuthError) {
          openModal(
            <OAuthModalMicrosoft
              oauthUrl={err.oauthUrl}
              onClose={() => openModal(<MicrosoftFileBrowser {...props} />)}
            />
          );
        } else {
          setError('Failed to authenticate with Microsoft');
          setAuthLoading(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load initial content
  useEffect(() => {
    if (!accessToken) return;

    setIsLoading(true);
    graph.getMyDrive(accessToken)
      .then((drive) => {
        setCurrentDriveId(drive.id);
        return graph.getMyDriveRoot(accessToken);
      })
      .then((driveItems) => {
        setItems(driveItems);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
      });

    // Load SharePoint sites if scope allows
    if (hasSharePointScope) {
      graph.getSharePointSites(accessToken)
        .then(setSites)
        .catch(console.error);
    }
  }, [accessToken, hasSharePointScope]);

  // Navigation
  const navigateToFolder = useCallback((item: graph.DriveItem) => {
    if (!accessToken) return;

    const actualId = item.remoteItem?.id || item.id;
    const actualDriveId = item.remoteItem?.parentReference?.driveId || item.parentReference?.driveId || currentDriveId;

    setIsLoading(true);
    setSelectedItem(null);
    setSearchQuery('');
    setError(null);

    setBreadcrumb(prev => [...prev, { id: actualId, name: item.name }]);
    setCurrentDriveId(actualDriveId);

    graph.getDriveItemChildren(accessToken, actualDriveId, actualId)
      .then(setItems)
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [accessToken, currentDriveId]);

  const navigateToBreadcrumb = useCallback((index: number) => {
    if (!accessToken) return;

    setIsLoading(true);
    setSelectedItem(null);
    setSearchQuery('');
    setError(null);

    if (index === -1) {
      setBreadcrumb([]);

      if (currentLocation === 'shared') {
        graph.getSharedWithMe(accessToken)
          .then(setItems)
          .catch((err) => setError(err.message))
          .finally(() => setIsLoading(false));
      } else {
        graph.getDriveRoot(accessToken, currentDriveId)
          .then(setItems)
          .catch((err) => setError(err.message))
          .finally(() => setIsLoading(false));
      }
    } else {
      const entry = breadcrumb[index];
      setBreadcrumb(prev => prev.slice(0, index + 1));

      graph.getDriveItemChildren(accessToken, currentDriveId, entry.id)
        .then(setItems)
        .catch((err) => setError(err.message))
        .finally(() => setIsLoading(false));
    }
  }, [accessToken, currentDriveId, currentLocation, breadcrumb]);

  const switchToMyDrive = useCallback(() => {
    if (!accessToken)
      return;

    setIsLoading(true);
    setSelectedItem(null);
    setBreadcrumb([]);
    setSearchQuery('');
    setError(null);
    setCurrentLocation('my-drive');

    graph.getMyDrive(accessToken)
      .then((drive) => {
        setCurrentDriveId(drive.id);
        return graph.getMyDriveRoot(accessToken);
      })
      .then(setItems)
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [accessToken]);

  const switchToShared = useCallback(() => {
    if (!accessToken) return;

    setIsLoading(true);
    setSelectedItem(null);
    setBreadcrumb([]);
    setSearchQuery('');
    setError(null);
    setCurrentLocation('shared');

    graph.getSharedWithMe(accessToken)
      .then(setItems)
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [accessToken]);

  const switchToSiteDrive = useCallback((driveId: string) => {
    if (!accessToken) return;

    setIsLoading(true);
    setIsSearching(false);
    setSelectedItem(null);
    setBreadcrumb([]);
    setSearchQuery('');
    setError(null);
    setCurrentLocation('site-drive');
    setCurrentDriveId(driveId);

    graph.getDriveRoot(accessToken, driveId)
      .then(setItems)
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [accessToken]);

  const toggleSiteExpanded = useCallback((siteId: string) => {
    setExpandedSites(prev => {
      const next = new Set(prev);
      if (next.has(siteId)) {
        next.delete(siteId);
      } else {
        next.add(siteId);
        if (!siteDrives[siteId] && accessToken) {
          graph.getSiteDrives(accessToken, siteId)
            .then((drives) => setSiteDrives(prev => ({ ...prev, [siteId]: drives })))
            .catch(console.error);
        }
      }
      return next;
    });
  }, [accessToken, siteDrives]);

  // Search
  const handleSearch = useCallback(() => {
    if (!accessToken || !currentDriveId)
      return;

    setIsSearching(true);
    setSelectedItem(null);
    setError(null);

    if (debouncedSearch)
      graph.searchDrive(accessToken, currentDriveId, debouncedSearch.trim())
        .then(setItems)
        .catch((err) => setError(err.message))
        .finally(() => setIsSearching(false));
    else
      switchToSiteDrive(currentDriveId);

  }, [accessToken, currentDriveId, debouncedSearch, switchToSiteDrive]);

  useEffect(() => {
    handleSearch();
  }, [debouncedSearch, handleSearch]);

  // Filtering
  function isFileVisible(item: graph.DriveItem): boolean {
    if (item.folder || item.remoteItem?.folder) return true;
    if (mode === 'folder') return false;
    return true;
  }

  function isFileAllowed(item: graph.DriveItem): boolean {
    if (item.folder || item.remoteItem?.folder) return true;
    if (!allowedExtensions) return true;
    const name = item.name.toLowerCase();
    return allowedExtensions.some(ext => name.endsWith(ext));
  }

  function isItemSelectable(item: graph.DriveItem): boolean {
    if (mode === 'folder') return !!(item.folder || item.remoteItem?.folder);
    if (item.folder || item.remoteItem?.folder) return false;
    return isFileAllowed(item);
  }

  // Selection
  function handleItemClick(item: graph.DriveItem) {
    if (item.folder || item.remoteItem?.folder) {
      if (mode === 'folder') {
        setSelectedItem(item);
      } else {
        navigateToFolder(item);
      }
    } else if (isItemSelectable(item)) {
      setSelectedItem(item);
    }
  }

  function handleItemDoubleClick(item: graph.DriveItem) {
    if (item.folder || item.remoteItem?.folder) {
      navigateToFolder(item);
    } else if (isItemSelectable(item)) {
      confirmSelection(item);
    }
  }

  function confirmSelection(item?: graph.DriveItem) {
    const selected = item || selectedItem;
    if (!selected) return;

    const driveId = selected.remoteItem?.parentReference?.driveId || selected.parentReference?.driveId || currentDriveId;
    const fileId = selected.remoteItem?.id || selected.id;
    props.onSelect(fileId, driveId);
    closeModal();
  }

  function getLocationName(): string {
    if (currentLocation === 'my-drive') return t('My OneDrive');
    if (currentLocation === 'shared') return t('Shared');
    return t('SharePoint');
  }

  function formatSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  function formatDate(dateStr?: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  // Filter and sort items
  const visibleItems = items.filter(isFileVisible);
  const sortedItems = [...visibleItems].sort((a, b) => {
    const aIsFolder = !!(a.folder || a.remoteItem?.folder);
    const bIsFolder = !!(b.folder || b.remoteItem?.folder);
    if (aIsFolder && !bIsFolder) return -1;
    if (!aIsFolder && bIsFolder) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <Modal size="fit">
      <div className="flex flex-col w-[800px] border rounded overflow-hidden">
        <div className='flex h-full border-b'>
          {/* Left Sidebar */}
          <div className="w-52 flex-shrink-0 border-r bg-gray-50 overflow-y-auto">
            <div className="p-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {t('Locations')}
            </div>

            {/* My OneDrive */}
            <button
              className={`flex items-center w-full px-3 py-2 text-sm font-semibold transition-colors ${currentLocation === 'my-drive' && breadcrumb.length === 0
                ? 'bg-blue bg-opacity-10 text-blue'
                : 'text-gray-700 qhover:bg-gray-100'
                }`}
              onClick={switchToMyDrive}
            >
              <HomeIcon className="w-4 h-4 mr-2 flex-shrink-0" />
              {t('My OneDrive')}
            </button>

            {/* Shared with me */}
            <button
              className={`flex items-center w-full px-3 py-2 text-sm font-semibold transition-colors ${currentLocation === 'shared' && breadcrumb.length === 0
                ? 'bg-blue bg-opacity-10 text-blue'
                : 'text-gray-700 hover:bg-gray-100'
                }`}
              onClick={switchToShared}
            >
              <ShareIcon className="w-4 h-4 mr-2 flex-shrink-0" />
              {t('Shared')}
            </button>

            {!authLoading &&
              <>
                <div className="p-3 pb-1 pt-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  SharePoint
                </div>

                {hasSharePointScope ? (
                  <>
                    {sites.length === 0 && (
                      <div className="px-3 py-2 text-xs text-gray-400">{t('No sites found')}</div>
                    )}
                    {sites.map((site) => (
                      <div key={site.id}>
                        <button
                          className="flex items-center w-full px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                          onClick={() => toggleSiteExpanded(site.id)}
                        >
                          {expandedSites.has(site.id)
                            ? <ChevronDownIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                            : <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                          }
                          <OfficeBuildingIcon className="w-4 h-4 mr-1.5 flex-shrink-0" />
                          <span className="truncate text-left">{site.displayName || site.name}</span>
                        </button>
                        {expandedSites.has(site.id) && (
                          <div className="ml-5">
                            {siteDrives[site.id] ? (
                              siteDrives[site.id].length > 0 ? (
                                siteDrives[site.id].map((drive) => (
                                  <button
                                    key={drive.id}
                                    className={`flex items-center w-full px-3 py-1.5 text-sm transition-colors ${currentLocation === 'site-drive' && currentDriveId === drive.id
                                      ? 'bg-blue bg-opacity-10 text-blue font-semibold'
                                      : 'text-gray-600 hover:bg-gray-100 font-medium'
                                      }`}
                                    onClick={() => switchToSiteDrive(drive.id)}
                                  >
                                    <CollectionIcon className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                                    <span className="truncate text-left">{drive.name}</span>
                                  </button>
                                ))
                              ) : (
                                <div className="px-3 py-1.5 text-xs text-gray-400">{t('No drives')}</div>
                              )
                            ) : (
                              <div className="px-3 py-1.5 text-xs text-gray-400">{t('Loading...')}</div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="px-3 py-2 text-xs text-gray-400">
                    {t('No SharePoint sites found.')}
                  </div>
                )}
              </>
            }
          </div>

          {/* Right Content */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Search */}
            <div className="flex items-center border-b px-3 py-2 gap-2">
              <SearchIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                className="flex-1 text-sm outline-none bg-transparent"
                placeholder={t('Search in current location...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Breadcrumb */}
            <div className="flex items-center border-b px-3 py-2 text-sm overflow-x-auto flex-shrink-0 gap-0">
              <button
                className="font-semibold text-blue hover:underline flex-shrink-0"
                onClick={() => navigateToBreadcrumb(-1)}
              >
                {getLocationName()}
              </button>
              {breadcrumb.map((entry, index) => (
                <div key={`${entry.id}-${index}`} className="flex items-center flex-shrink-0">
                  <ChevronRightIcon className="w-3 h-3 mx-1 text-gray-400" />
                  <button
                    className={`font-semibold hover:underline ${index === breadcrumb.length - 1 ? 'text-gray-700' : 'text-blue'
                      }`}
                    onClick={() => navigateToBreadcrumb(index)}
                  >
                    {entry.name}
                  </button>
                </div>
              ))}
            </div>

            {/* File list */}
            <div className="flex flex-col overflow-y-scroll h-[460px]">
              {(isLoading || isSearching) ? (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm font-semibold">
                  {t('Loading...')}
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center h-full gap-2 px-4">
                  <div className="text-red text-sm font-semibold text-center">{error}</div>
                  <button className="text-xs text-blue font-semibold hover:underline" onClick={() => navigateToBreadcrumb(-1)}>
                    {t('Go back')}
                  </button>
                </div>
              ) : sortedItems.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm font-semibold">
                  {mode === 'folder' ? t('No folders found') : t('No items found')}
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {sortedItems.map((item) => {
                    const isFolder = !!(item.folder || item.remoteItem?.folder);
                    const selectable = isItemSelectable(item);
                    const allowed = isFileAllowed(item);
                    const isSelected = selectedItem?.id === item.id;

                    return (
                      <div
                        key={item.id}
                        className={`flex items-center px-3 py-2 text-sm transition-colors select-none ${isSelected
                          ? 'bg-blue bg-opacity-10'
                          : isFolder
                            ? 'hover:bg-gray-50 cursor-pointer'
                            : selectable
                              ? 'hover:bg-gray-50 cursor-pointer'
                              : 'opacity-40 cursor-default'
                          }`}
                        onClick={() => {
                          if (isFolder || selectable) handleItemClick(item);
                        }}
                        onDoubleClick={() => {
                          if (isFolder || selectable) handleItemDoubleClick(item);
                        }}
                      >
                        {isFolder
                          ? <FolderIcon className="w-6 h-6 mr-2 text-blue flex-shrink-0" />
                          : <img src={MIMETYPE_ICON_MAP[item.file!.mimeType]} className={`w-6 h-6 mr-2 text-gray-400 flex-shrink-0 ${allowed ? 'text-gray-400' : 'text-gray-300'}`} alt="File icon" />
                        }
                        <div className="flex-1 min-w-0">
                          <div className={`font-semibold truncate ${isFolder ? 'text-gray-700' : allowed ? 'text-gray-600' : 'text-gray-400'
                            }`}>
                            {item.name}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 ml-3 flex-shrink-0 text-xs text-gray-400">
                          {!isFolder && item.size !== undefined && (
                            <span className="w-16 text-right">{formatSize(item.size)}</span>
                          )}
                          {item.lastModifiedDateTime && (
                            <span className="w-24 text-right">{formatDate(item.lastModifiedDateTime)}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Selected item indicator */}
        <div className='flex items-center p-2 flex-shrink-0'>
          {selectedItem && (
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-600">
              {mode === 'folder'
                ? <FolderIcon className="w-4 h-4 text-blue flex-shrink-0" />
                : <img src={MIMETYPE_ICON_MAP[selectedItem.file!.mimeType]} className="w-6 h-6 text-gray-400 flex-shrink-0" alt="File icon" />
              }
              <span className="truncate">{t('Selected')}: {selectedItem.name}</span>
            </div>
          )}
          <button className='btn btn-blue ml-auto' disabled={!selectedItem} onClick={() => confirmSelection()}>
            Select
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default MicrosoftFileBrowser;
